import { useQuery } from "@tanstack/react-query"
import { Button, Divider, Skeleton } from "antd"
import { useRef } from "react"
import styled, { useTheme } from "styled-components"
import { getPrice, images, ShowMessagePage } from "../../config"
import { getProducts } from "../../endpoints/"
import { OrderInfoInterface } from "../../types"
import { FadeIn, ShortListContainer } from "../GeneralComponents"
import { Product } from "./Product"

const Title = styled.h1`
    text-align: center;
    font-size: 1.618rem;
    margin-top: 2em;
    margin-bottom: 2em;
`

const FlexContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px 40px;
    margin-bottom: 2em;
`
// const ProductDetails={
//     4: `PCR verifier        | Find consensus of your PCR products
// All major bands will be reported
// Optionally, if you provide a template of your PCR products,we will report any mutations, insertions, or deletions in your PCR product`,
//     7: `PolyA vector QC     | For plasmids with a long polyA sequence in the template
// The full template sequence is required at the time of order submisission
// Provides polyA length estimation in addition to QC analysis`,
//     -6: `Plasmid Sequencing  | Find consensus of your plasmid DNA
// Sample is assumed to be clonal, and only one consensus will be reported
// Optionally, you can provide a template sequence, either full length or a fragment
// Results will be delivered every Saturday`,
//     -1: `Amplicon Sequencing | Find consensus of your amplicons (PCR products)
// Template is required`,
//     2: `Plasmid QC          | Find consensus of your plamsid DNA
// If multiple plasmids are present due to recombination, deletion, duplication, or accidental mixing, we will report them all
// Optionally, if you provide a template sequence, we will report mutations, insertions, and deletions in your plasmid`,
//     6: `RCA QC              | Find consensus of the RCA products of your plasmids
// If multiple plasmids are present due to recombination, deletion, duplication, or accidental mixing, we will report them all
// Optionally, if you provide a template sequence, we will report mutations, insertions, and deletions in your plasmid`,
// }
const ProductDetails: { [key: string]: [string, string] } = {
    "-1": [`Find consensus and mutations in your amplicon DNA (PCR products)
The first and last 10-15 bases may be missed from the report
Template sequence is optional`,
'Amplicon Sequencing'], 
    "2": [`Find consensus and mutations in your plasmid DNA
Template sequence is optional`,
'Plasmid QC'],
    "6": [`Find consensus and mutations in your RCA products
Template sequence is optional`,'RCA QC'],
    "7": [`For plasmids with a long polyA sequence in the template
The full template sequence is required at the time of order submission
Provides polyA length estimation in addition to QC analysis`,'PolyA vector QC'],
    "4": [`Find consensus of your PCR products
Template is optional`,'PCR verifier'],
    "-6": [`Find consensus of your plasmid DNA
Sample is assumed to be clonal, and only one consensus will be reported
Optionally, you can provide a template sequence, either full length or a fragment
Results will be delivered every Saturday`,'Plasmid Sequencing'],
}
interface ChooseServiceProps {
    orderInfo: OrderInfoInterface
    setOrderInfo: React.Dispatch<React.SetStateAction<OrderInfoInterface>>
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>
}

export const ChooseService = (props: ChooseServiceProps): JSX.Element => {
    const theme = useTheme()
    const nodeRef = useRef(null)
    const { isPending, isError, data, error, isSuccess } = useQuery({
        queryKey: ["getproducts"],
        queryFn: getProducts,
    })
    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return ShowMessagePage("Error", error.message)
    }
    if (isSuccess) {
        if (data.code !== 1000) {
            return ShowMessagePage("Error", "Cannot fetch product information.")
        }
    }
    const PRODUCTS = data.products

    // consolelog('products is',PRODUCTS)
    return (
        <>
            <Title>Please choose a service to order</Title>
            <FlexContainer>
                {PRODUCTS.map((p, index) => {
                    // console.log(p.image)
                    // console.log(images(`./${p.image}`))
                    return (
                        <Product
                            key={p.id}
                            id={p.id}
                            highlight={props.orderInfo.productid === p.id}
                            title={p.name}
                            image={images(`./${p.image}`)}
                            price={getPrice(p.price) ?? 0}
                            defaultConcentration={p.defaultConcentration}
                            text={p.shortDesc}
                            orderInfo={props.orderInfo}
                            setOrderInfo={props.setOrderInfo}
                            setCurrentStep={props.setCurrentStep}
                        />
                    )
                })}
            </FlexContainer>
            <div style={{ height: "30em" }}>
                <FadeIn time={1}>
                    <div ref={nodeRef} style={{ height: "29em" }}>
                        {props.orderInfo.productid !== null && (
                            <ShortListContainer>
                                <Divider>{PRODUCTS.find((x) => x.id === props.orderInfo.productid)?.name}</Divider>

                                <ul
                                    style={{
                                        textAlign: "left",
                                        listStylePosition: "inside",
                                        listStyleType: "disc",
                                        marginLeft: "1rem",
                                        marginRight: "1rem",
                                        paddingLeft: 0,
                                    }}
                                >
                                    {ProductDetails[(data?.products
                                        .find((x) => x.id === props.orderInfo.productid)?.id??2)+'']?.[0]
                                        ?.split("\n")
                                        .map((d, i) => (
                                            <li key={i} id={i + ""} style={{ lineHeight: "2em", margin: "10px 0" }}>
                                                {d}
                                            </li>
                                        ))}
                                </ul>
                                <div style={{ textAlign: "center", marginTop: "1em" }}>
                                    <Button
                                        size="large"
                                        type="primary"
                                        disabled={props.orderInfo.productid === null}
                                        style={{
                                            fontSize: "1.2rem",
                                            backgroundColor: theme.primary,
                                            boxShadow: "1px 1px 2px 2px rgba(0, 0, 0, 0.5)",
                                        }}
                                        onClick={() => {
                                            props.setCurrentStep(1)
                                            // Scroll to top when moving to next step
                                            window.scrollTo({ top: 0, behavior: 'smooth' })
                                        }}
                                    >
                                        Next Step
                                    </Button>
                                </div>
                            </ShortListContainer>
                        )}
                    </div>
                </FadeIn>
            </div>
        </>
    )
}
